import { useEffect, useState } from 'react';
import { Link, api } from '../lib.jsx';
import { Modal, PlatformDot, Wordmark } from '../ui.jsx';

// The kind of thing people post, as Yapscape labels it. Illustrative only: no real accounts.
const FEED = [
  ['tiktok', 'Honestly the best moisturiser I’ve tried this year.', 'Praise', 'good'],
  ['reddit', 'Wish the pump didn’t break after two weeks.', 'Complaint', 'bad'],
  ['instagram', 'Does it work for sensitive skin?', 'Question', 'info'],
  ['youtube', 'Cheaper than the big brands and does the same job.', 'Praise', 'good'],
  ['amazon', 'Smells lovely, but the tub arrived half empty.', 'Complaint', 'bad'],
  ['linkedin', 'Which retailers stock the travel size?', 'Question', 'info'],
];

/** Sign-in pages: the brand and what Yapscape does on the left, the form on the right. */
function Shell({ children }) {
  return (
    <div className="auth">
      <aside className="auth-brand">
        <span className="wordmark"><Wordmark /></span>
        <div className="auth-pitch">
          <p className="eyebrow">Social listening</p>
          <h1>Ask a question. Get the answer people are <em>already posting</em>.</h1>
          <p className="auth-lede">Yapscape reads public posts and comments on TikTok, Instagram, Reddit, YouTube and LinkedIn, and Amazon reviews, then tells you what people praise, criticise and ask about.</p>
        </div>
        <div className="auth-feed" aria-hidden="true">
          <div className="auth-feed-track">
            {[...FEED, ...FEED].map(([p, text, tag, tone], i) => (
              <div className="feed-card" key={i}>
                <span className="fc-top"><PlatformDot p={p} /><span className={`fc-tag ${tone}`}>{tag}</span></span>
                <p>“{text}”</p>
              </div>
            ))}
          </div>
        </div>
        <div className="auth-summary" aria-hidden="true">
          <span className="as-head"><b>What people say</b><span>every finding links to its comments</span></span>
          <span className="as-bar"><i style={{ flex: 58, background: 'var(--pos)' }} /><i style={{ flex: 17, background: 'var(--mix)' }} /><i style={{ flex: 25, background: 'var(--neg)' }} /></span>
          <span className="as-legend"><span><i style={{ background: 'var(--pos)' }} />58% positive</span><span><i style={{ background: 'var(--mix)' }} />17% mixed</span><span><i style={{ background: 'var(--neg)' }} />25% negative</span></span>
        </div>
      </aside>
      <main className="auth-main">
        <div className="auth-form">{children}</div>
        <p className="auth-foot">No account yet? <Link to="/demo">See a sample report</Link></p>
      </main>
    </div>
  );
}

/** Email and password. "Forgot password" emails a link to choose a new one (also how pilot users set their first). */
export function SignIn({ onDone }) {
  const [f, setF] = useState({ email: '', password: '' });
  const [forgot, setForgot] = useState(false);
  const [sent, setSent] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      if (forgot) setSent(await api('/api/auth/forgot', { body: { email: f.email } }));
      else { await api('/api/auth/login', { body: f }); onDone(); }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }
  const mode = (on) => () => { setForgot(on); setError(null); setSent(null); };

  if (sent) return (
    <Shell>
      <div className="signin-body">
        <h2>Check your email</h2>
        <p>{sent.message}</p>
        {sent.devNotice ? <div className="warn-box">{sent.devNotice}</div> : null}
        <button className="btn ghost" onClick={mode(false)}>Back to sign in</button>
      </div>
    </Shell>
  );
  return (
    <Shell>
      <form onSubmit={submit}>
        <div className="auth-head">
          <h2>{forgot ? 'Choose a new password' : <>Welcome <em>back</em></>}</h2>
          <p className="muted">{forgot ? 'We email you a link to choose a new password.' : 'Sign in with the email and password the Yapscape team sent you.'}</p>
        </div>
        <label className="field">Work email
          <input type="email" autoComplete="email" required autoFocus value={f.email} onChange={set('email')} />
        </label>
        {forgot ? null : (
          <label className="field">Password
            <input type="password" autoComplete="current-password" required value={f.password} onChange={set('password')} />
          </label>
        )}
        {error ? <div className="error-box" role="alert">{error}</div> : null}
        <button className="btn primary" disabled={busy}>{busy ? 'One moment…' : forgot ? 'Email me a link' : 'Sign in'}</button>
        <button type="button" className="btn ghost small" onClick={mode(!forgot)}>{forgot ? 'Back to sign in' : 'Forgot password?'}</button>
      </form>
    </Shell>
  );
}

/** The emailed invite or reset link lands here. The token is in the URL fragment, so it never reaches server logs. */
export function SetPassword({ onDone }) {
  const [token] = useState(() => new URLSearchParams(location.hash.slice(1)).get('token'));
  const [info, setInfo] = useState(null);
  const [password, setPassword] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    history.replaceState(null, '', '/set-password');
    if (!token) { setError('This link is incomplete. Ask for a new one with "Forgot password".'); return; }
    api('/api/auth/token', { body: { token } }).then(setInfo, (e) => setError(e.message));
  }, []);

  async function submit(e) {
    e.preventDefault();
    setBusy(true); setError(null);
    try { await api('/api/auth/set-password', { body: { token, password } }); onDone(); }
    catch (err) { setError(err.message); }
    finally { setBusy(false); }
  }

  if (!info) return (
    <Shell>
      <div className="signin-body">
        {error ? <><div className="error-box">{error}</div><Link className="btn" to="/">Back to sign in</Link></> : <p className="muted">Checking your link…</p>}
      </div>
    </Shell>
  );
  const invite = info.purpose === 'invite';
  return (
    <Shell>
      <form onSubmit={submit}>
        <div className="auth-head">
          <h2>{invite ? `Welcome, ${info.name ? info.name.split(' ')[0] : info.company}` : 'Choose a new password'}</h2>
          <p className="muted">{invite ? `Choose a password for ${info.company}'s Yapscape account.` : 'Saving signs this account out of every other browser.'}</p>
        </div>
        {/* Shown read-only so password managers save it as the username. */}
        <label className="field">Email
          <input type="email" autoComplete="username" value={info.email} readOnly />
        </label>
        <label className="field">New password <span className="hint">at least 10 characters</span>
          <input type="password" autoComplete="new-password" required minLength={10} maxLength={200} autoFocus value={password} onChange={(e) => setPassword(e.target.value)} />
        </label>
        {error ? <div className="error-box" role="alert">{error}</div> : null}
        <button className="btn primary" disabled={busy}>{busy ? 'Saving…' : invite ? 'Set password and continue' : 'Save and sign in'}</button>
      </form>
    </Shell>
  );
}

/** The signed-in client changes their password: the current one first. Other browsers are signed out. */
export function ChangePassword({ onClose }) {
  const [f, setF] = useState({ current: '', password: '' });
  const [state, setState] = useState(null); // null | 'saving' | 'done' | an error message
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  async function save() {
    setState('saving');
    try { await api('/api/auth/change-password', { body: f }); setState('done'); } catch (e) { setState(e.message); }
  }
  if (state === 'done') return (
    <Modal title="Password changed" onClose={onClose} actions={<button className="btn primary" onClick={onClose}>Done</button>}>
      <p>Use your new password next time. Any other browser signed in to this account was signed out.</p>
    </Modal>
  );
  return (
    <Modal title="Change your password" onClose={onClose}
      actions={<><button className="btn ghost" onClick={onClose}>Cancel</button><button className="btn primary" disabled={state === 'saving' || !f.current || f.password.length < 10} onClick={save}>{state === 'saving' ? 'Saving…' : 'Change password'}</button></>}>
      <label className="field">Current password<input type="password" autoComplete="current-password" value={f.current} onChange={set('current')} autoFocus /></label>
      <label className="field">New password <span className="hint">at least 10 characters</span><input type="password" autoComplete="new-password" minLength={10} maxLength={200} value={f.password} onChange={set('password')} /></label>
      {state && state !== 'saving' ? <div className="error-box" role="alert">{state}</div> : null}
    </Modal>
  );
}

/**
 * An assistant (claude.ai, ChatGPT) asks to connect: the signed-in client allows it or not, then returns to it. The
 * server checked the request; the name is the assistant's own, so where the client returns is shown too.
 */
export function Authorize({ user }) {
  const [query] = useState(() => location.search.slice(1));
  const [info, setInfo] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api(`/api/oauth/authorize?${query}`).then(setInfo, (e) => setError(e.message)); }, []);
  async function answer(allow) {
    setBusy(true); setError(null);
    try { location.href = (await api('/api/oauth/authorize', { body: { query, allow } })).redirect; }
    catch (e) { setError(e.message); setBusy(false); }
  }
  if (!info) return (
    <Shell>
      <div className="signin-body">
        {error ? <><div className="error-box">{error}</div><Link className="btn" to="/">Go to Yapscape</Link></> : <p className="muted">Checking the request…</p>}
      </div>
    </Shell>
  );
  return (
    <Shell>
      <div className="signin-body">
        <div className="auth-head">
          <h2>Connect <em>{info.client}</em> to Yapscape?</h2>
          <p className="muted">Signed in as {user.email} ({user.workspace.name}). After you choose, you go back to <b>{info.via}</b>.</p>
        </div>
        <div>
          <p className="small"><b>It will be able to</b></p>
          <ul className="plain-list small">
            <li>Start research for this account, using its credits</li>
            <li>See this account's research, reports and the comments behind them</li>
            <li>Stop research that is running</li>
          </ul>
          <p className="small"><b>It will not</b> see your password, change your account or make keys. You can disconnect it at any time on the MCP page.</p>
        </div>
        {error ? <div className="error-box" role="alert">{error}</div> : null}
        <button className="btn primary" disabled={busy} onClick={() => answer(true)}>{busy ? 'One moment…' : 'Allow'}</button>
        <button type="button" className="btn ghost" disabled={busy} onClick={() => answer(false)}>Cancel</button>
      </div>
    </Shell>
  );
}
